import React, { useState, useEffect } from 'react';
import { Search, Phone, User, Heart, Users, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface SOSContact {
    id: number;
    user_id: number;
    user_username: string;
    user_name?: string;
    user_avatar?: string;
    name: string;
    phone: string;
    relationship: string;
    is_active: boolean;
    created_at: string;
}

interface SOSStats {
    total_contacts: number;
    active_contacts: number;
    users_with_contacts: number;
    by_relationship: { relationship: string; count: number }[];
}

const SOSContactManagement: React.FC = () => {
    const [contacts, setContacts] = useState<SOSContact[]>([]);
    const [stats, setStats] = useState<SOSStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const [contactsRes, statsRes] = await Promise.all([
                axios.get('https://api.lifeshare.app/api/v1/admin/sos-contacts'),
                axios.get('https://api.lifeshare.app/api/v1/admin/sos-contacts/stats')
            ]);
            setContacts(contactsRes.data.contacts || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const getRelationshipIcon = (rel: string) => {
        const lower = rel.toLowerCase();
        if (lower.includes('eş') || lower.includes('spouse')) return '💑';
        if (lower.includes('anne') || lower.includes('mother')) return '👩';
        if (lower.includes('baba') || lower.includes('father')) return '👨';
        if (lower.includes('çocuk') || lower.includes('child')) return '👶';
        if (lower.includes('kardeş') || lower.includes('sibling')) return '👫';
        if (lower.includes('arkadaş') || lower.includes('friend')) return '🤝';
        return '👤';
    };

    const filteredContacts = contacts.filter(c =>
        c.user_username.toLowerCase().includes(searchText.toLowerCase()) ||
        c.name.toLowerCase().includes(searchText.toLowerCase()) ||
        c.phone.includes(searchText)
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">SOS Kişileri</h1>
                        <p className="text-sm text-gray-500 mt-1">Acil durum kişilerini görüntüle</p>
                    </div>
                    <button onClick={fetchData} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Phone className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-red-700">{stats.total_contacts}</p>
                            <p className="text-xs text-red-600">Toplam</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Heart className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-green-700">{stats.active_contacts}</p>
                            <p className="text-xs text-green-600">Aktif</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Users className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-blue-700">{stats.users_with_contacts}</p>
                            <p className="text-xs text-blue-600">Kullanıcı</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                            <p className="text-2xl font-bold text-purple-700">{stats.users_with_contacts > 0 ? (stats.total_contacts / stats.users_with_contacts).toFixed(1) : 0}</p>
                            <p className="text-xs text-purple-600">Ort. Kişi/Kullanıcı</p>
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="bg-white rounded-2xl p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" placeholder="Kullanıcı, kişi adı veya telefon ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                </div>

                {/* Relationships */}
                {stats && stats.by_relationship.length > 0 && (
                    <div className="bg-white rounded-2xl p-4">
                        <h3 className="font-medium text-gray-900 mb-3">👥 İlişki Türleri</h3>
                        <div className="flex flex-wrap gap-2">
                            {stats.by_relationship.map((item, index) => (
                                <span key={index} className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-sm">
                                    {getRelationshipIcon(item.relationship)} {item.relationship} <span className="text-purple-400">({item.count})</span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* List */}
                {filteredContacts.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <Phone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">SOS kişisi bulunamadı</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {filteredContacts.map((contact) => (
                            <div key={contact.id} className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-100 transition-all">
                                <div className="flex items-start gap-3">
                                    {contact.user_avatar ? (
                                        <img src={`https://api.lifeshare.app${contact.user_avatar}`} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
                                            <User className="w-5 h-5" />
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="font-semibold text-gray-900 text-sm truncate">{contact.user_name || contact.user_username}</p>
                                        <p className="text-xs text-gray-500">@{contact.user_username}</p>
                                    </div>
                                    <span className={`px-2 py-0.5 rounded-xl text-xs font-medium ${contact.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                        {contact.is_active ? 'Aktif' : 'Pasif'}
                                    </span>
                                </div>

                                <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-lg">{getRelationshipIcon(contact.relationship)}</span>
                                        <span className="font-medium text-gray-900">{contact.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <Phone className="w-4 h-4" />
                                        <a href={`tel:${contact.phone}`} className="text-purple-600 hover:underline">{contact.phone}</a>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">{contact.relationship}</p>
                                </div>

                                <div className="mt-2 text-xs text-gray-400 text-right">
                                    {new Date(contact.created_at).toLocaleDateString('tr-TR')}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SOSContactManagement;
